import { enforceGridPrecisionInMeters } from "@buildwithflux/shared";
import { parse } from "kicad-to-json";
import { mapValues } from "lodash";
import SparkMD5 from "spark-md5";
import { AbstractKicadImporter, KicadLayerDesignators, KicadPadShape, KicadPadType, } from "../../../";
import { convertKicadFootprintToFluxFootprint } from "./KicadToFlux";
function matches(value) {
    return (mightMatch) => mightMatch === value;
}
const kicadOrigin = { x: 0, y: 0 };
function isKicadPadType(type) {
    return Object.values(KicadPadType).some((ty) => ty === type);
}
function isKicadPadShape(shape) {
    return Object.values(KicadPadShape).some((sh) => sh === shape);
}
export class KiCadFootprintImporter extends AbstractKicadImporter {
    get moduleHash() {
        return SparkMD5.hash(this.moduleData);
    }
    parse() {
        return mapValues(this.parseToKicadTypes(), (module) => {
            return convertKicadFootprintToFluxFootprint(module, this.moduleHash);
        });
    }
    parseToKicadTypes() {
        const json = parse.module(this.moduleData);
        const parsedModule = this.parseKicadModule(json.module);
        return { [parsedModule.name]: parsedModule };
    }
    parseKicadModule(module) {
        const moduleName = module.name;
        const moduleLayer = this.getCanonicalLayer(module.layer);
        const moduleTEdit = module.tedit;
        const modulePosition = module.at ?? kicadOrigin;
        const mod = {
            name: moduleName,
            layer: moduleLayer,
            tedit: moduleTEdit,
            at: modulePosition,
            tags: module.tags,
        };
        const textElements = this.getFootprintTextElements(module.fp_text ?? []);
        if (textElements.length > 0)
            mod["fp_text"] = textElements;
        const lineElements = this.getFootprintLineElements(module.fp_line ?? []);
        if (lineElements.length > 0)
            mod["fp_line"] = lineElements;
        const circleElements = this.getFootprintCircleElements(module.fp_circle ?? []);
        if (circleElements.length > 0)
            mod["fp_circle"] = circleElements;
        const arcElements = this.getFootprintArcElements(module.fp_arc ?? []);
        if (arcElements.length > 0)
            mod["fp_arc"] = arcElements;
        const polyElements = this.getFootprintPolyElements(module.fp_poly ?? []);
        if (polyElements.length > 0)
            mod["fp_poly"] = polyElements;
        const pads = this.getFootprintPadElements(module.pad ?? []);
        if (pads.length > 0)
            mod["pad"] = pads;
        const models = this.getFootprintModelElements(module.model ?? []);
        if (models.length > 0)
            mod["model"] = models;
        return mod;
    }
    getFootprintModelElements(models) {
        return models.map((model) => this.rawFpModelElementToKicad(model));
    }
    rawFpModelElementToKicad(model) {
        return {
            model_id: SparkMD5.hash(JSON.stringify(model)),
            model_path: model.filename,
            at: model.at?.xyz,
            offset: model.offset?.xyz,
            scale: model.scale?.xyz,
            rotate: model.rotate?.xyz,
        };
    }
    getFootprintPadElements(elements) {
        return elements.map((el) => this.rawFpPadElementToKicad(el));
    }
    rawFpPadElementToKicad(element) {
        if (!isKicadPadType(element.pad_type)) {
            throw new Error(`unknown pad type ${element.pad_type}`);
        }
        if (!isKicadPadShape(element.pad_shape)) {
            throw new Error(`unknown pad shape ${element.pad_shape}`);
        }
        const pad = {
            pad_type: element.pad_type,
            layers: element.layers.map((l) => this.getCanonicalLayer(l)),
            pad_id: element.pad_id,
            pad_shape: element.pad_shape,
            size: element.size,
            at: element.at,
        };
        // optional properties, each done separately for proper typing
        if (element.locked !== undefined) {
            pad.locked = element.locked;
        }
        if (element.drill !== undefined) {
            pad.drill = element.drill;
        }
        // TODO: can't add this yet due to backward compatibility,
        // affecting pads on generic resistors and capacitors.
        // if (element.roundrect_rratio !== undefined) {
        //     pad.roundrect_rratio = element.roundrect_rratio;
        // }
        if (element.options !== undefined) {
            pad.options = element.options;
        }
        if (element.primitives !== undefined) {
            pad.primitives = element.primitives;
        }
        return pad;
    }
    getFootprintPolyElements(elements) {
        return elements.map((el) => this.rawFpPolyElementToKicad(el));
    }
    rawFpPolyElementToKicad(element) {
        return {
            pts: element.pts,
            layer: this.getCanonicalLayer(element.layer),
            width: element.stroke?.width ?? element.width ?? 0,
        };
    }
    getFootprintCircleElements(elements) {
        return elements.map((el) => this.rawFpCircleElementToKicad(el));
    }
    rawFpCircleElementToKicad(element) {
        return {
            center: element.center,
            end: element.end,
            layer: this.getCanonicalLayer(element.layer),
            width: element.stroke?.width ?? element.width ?? 0,
        };
    }
    getFootprintArcElements(elements) {
        return elements.map((el) => this.rawFpArcElementToKicad(el));
    }
    rawFpArcElementToKicad(element) {
        let { start, end, mid } = element;
        if (!mid) {
            // old format has `start` (as center), `end`, and `angle` sweep around circle,
            // but we want the `start`, `mid`, and `end` points along the arc
            const center = start;
            const angle = ((element.angle ?? 0) * Math.PI) / 180;
            const dx = end.x - center.x;
            const dy = end.y - center.y;
            const r = Math.sqrt(dx * dx + dy * dy);
            const endAngle = Math.atan2(dy, dx);
            start = {
                x: enforceGridPrecisionInMeters(center.x + r * Math.cos(endAngle + angle)),
                y: enforceGridPrecisionInMeters(center.y + r * Math.sin(endAngle + angle)),
            };
            mid = {
                x: enforceGridPrecisionInMeters(center.x + r * Math.cos(endAngle + angle / 2)),
                y: enforceGridPrecisionInMeters(center.y + r * Math.sin(endAngle + angle / 2)),
            };
        }
        return {
            start,
            end,
            mid,
            layer: this.getCanonicalLayer(element.layer),
            width: element.stroke?.width ?? element.width ?? 0,
        };
    }
    getFootprintLineElements(elements) {
        return elements.map((el) => this.rawFpLineElementToKicad(el));
    }
    rawFpLineElementToKicad(element) {
        return {
            start: element.start,
            end: element.end,
            layer: this.getCanonicalLayer(element.layer),
            width: element.stroke?.width ?? element.width ?? 0,
        };
    }
    getFootprintTextElements(elements) {
        return elements.map((el) => this.rawFpTextElementToKicad(el));
    }
    rawFpTextElementToKicad(element) {
        const layer = this.getCanonicalLayer(element.layer);
        const effects = this.getTextEffects(element.effects);
        return {
            text: element.text,
            type: element.type,
            at: element.at,
            layer: layer,
            effects: effects,
        };
    }
    getCanonicalLayer(layerName) {
        if (KicadLayerDesignators.some(matches(layerName))) {
            return layerName;
        }
        throw new Error(`unknown canonical layer ${layerName}`);
    }
    getTextEffects(effects) {
        return {
            font: {
                size: {
                    height: effects.font.size.height,
                    width: effects.font.size.width,
                },
                thickness: effects.font.thickness ?? 0,
            },
            justify: effects.justify ? [effects.justify] : [],
            hide: effects.hide ? true : false,
        };
    }
}
// Convert a kicad footprint to a flux footprint using the injected importer and
// return a map of flux footprints.
export const convertFootprintToFluxFootprintMap = (moduleData, importer) => {
    importer = importer ?? new KiCadFootprintImporter(moduleData);
    const moduleHash = SparkMD5.hash(moduleData);
    const result = {};
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    Object.values(importer.parse()).forEach((module) => {
        result[SparkMD5.hash(moduleHash + JSON.stringify(module))] = module;
    });
    return result;
};
//# sourceMappingURL=KiCadFootprintImporter.js.map